import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { getUnicodeIconCodes, isWebComponentsPrivateUseCode, parseIconCode, toIconEntity } from './iconCodeUtils';

interface IconPickerDialogProps {
    open: boolean;
    title: string;
    value: string;
    width?: number;
    height?: number;
    onApply: (code: number | null) => void;
    onClose: () => void;
}

const IconPickerDialog: React.FC<IconPickerDialogProps> = ({
    open,
    title,
    value,
    width = 980,
    height = 640,
    onApply,
    onClose,
}) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [codes, setCodes] = useState<number[]>([]);
    const [selectedCode, setSelectedCode] = useState<number | null>(null);
    const [tab, setTab] = useState<'kendo' | 'unicode'>('kendo');
    const [filterText, setFilterText] = useState('');

    const loadIconCodes = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const base = process.env.PUBLIC_URL || '';
            const resp = await fetch(`${base}/kendo-font-icons/index.css`);
            if (!resp.ok) {
                throw new Error(`HTTP ${resp.status}`);
            }

            const css = await resp.text();
            const matches = css.matchAll(/content:\s*"\\([a-fA-F0-9]+)"/g);
            const codeSet = new Set<number>();
            for (const match of matches) {
                const code = parseInt(match[1], 16);
                if (Number.isFinite(code) && code > 0) {
                    codeSet.add(code);
                }
            }

            const discoveredCodes = Array.from(codeSet).sort((a, b) => a - b);
            if (discoveredCodes.length === 0) {
                throw new Error('No icon codes discovered from font CSS');
            }

            setCodes(discoveredCodes);
            setLoading(false);
        } catch (err: any) {
            const fallbackCodes: number[] = [];
            for (let i = 0xe000; i <= 0xe2ff; i++) {
                fallbackCodes.push(i);
            }
            setCodes(fallbackCodes);
            setLoading(false);
            setError(`Loaded fallback icon set: ${err?.toString?.() ?? err}`);
        }
    }, []);

    useEffect(() => {
        if (!open) {
            return;
        }

        const currentCode = parseIconCode(value);
        setSelectedCode(currentCode);
        setTab(currentCode && !isWebComponentsPrivateUseCode(currentCode) ? 'unicode' : 'kendo');
        setFilterText('');

        if (codes.length === 0 && !loading) {
            void loadIconCodes();
        }
    }, [open, value, codes.length, loading, loadIconCodes]);

    const mergedCodes = useMemo(() => {
        const set = new Set<number>(codes);
        if (selectedCode !== null && Number.isFinite(selectedCode)) {
            set.add(selectedCode);
        }
        return Array.from(set).sort((a, b) => a - b);
    }, [codes, selectedCode]);

    const unicodeCodes = useMemo(() => {
        const set = new Set<number>(getUnicodeIconCodes());
        mergedCodes.forEach(code => {
            if (!isWebComponentsPrivateUseCode(code)) {
                set.add(code);
            }
        });
        return Array.from(set).sort((a, b) => a - b);
    }, [mergedCodes]);

    const visibleCodes = useMemo(() => {
        const baseCodes = tab === 'kendo'
            ? mergedCodes.filter(code => isWebComponentsPrivateUseCode(code))
            : unicodeCodes;

        const ft = filterText.trim();
        if (!ft) {
            return baseCodes;
        }

        return baseCodes.filter(code => String(code).includes(ft));
    }, [tab, mergedCodes, unicodeCodes, filterText]);

    const handleApply = useCallback(() => {
        onApply(selectedCode);
        onClose();
    }, [onApply, onClose, selectedCode]);

    if (!open) {
        return null;
    }

    return (
        <Dialog title={title} onClose={onClose} width={width} height={height}>
            <div style={{ padding: '12px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontSize: '12px', color: '#555' }}>Selected:</span>
                        <span
                            className={isWebComponentsPrivateUseCode(selectedCode) ? 'k-icon k-font-icon' : ''}
                            dangerouslySetInnerHTML={{ __html: toIconEntity(selectedCode) || '&#9633;' }}
                            style={{
                                fontSize: '22px',
                                width: '30px',
                                textAlign: 'center',
                                fontFamily: isWebComponentsPrivateUseCode(selectedCode) ? 'WebComponentsIcons' : 'inherit',
                                lineHeight: 1,
                            }}
                        />
                        <span style={{ fontSize: '12px', color: '#555' }}>{selectedCode ?? 'None'}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <button
                            type="button"
                            className="k-button k-button-sm"
                            onClick={() => setTab('kendo')}
                            style={{
                                background: tab === 'kendo' ? '#e6f2ff' : '#fff',
                                borderColor: tab === 'kendo' ? '#0d6efd' : '#ccc',
                            }}
                        >
                            Kendo Icons
                        </button>
                        <button
                            type="button"
                            className="k-button k-button-sm"
                            onClick={() => setTab('unicode')}
                            style={{
                                background: tab === 'unicode' ? '#e6f2ff' : '#fff',
                                borderColor: tab === 'unicode' ? '#0d6efd' : '#ccc',
                            }}
                        >
                            Unicode Icons
                        </button>
                    </div>
                    <input
                        type="text"
                        className="k-input k-input-sm"
                        placeholder={tab === 'kendo' ? 'Filter Kendo code (example: 57361)' : 'Filter Unicode code (example: 128203)'}
                        value={filterText}
                        onChange={(e) => setFilterText(e.target.value)}
                        style={{ width: '280px' }}
                    />
                </div>

                {error && <div className="packlists-error" style={{ marginBottom: '4px' }}>{error}</div>}

                <div style={{ border: '1px solid #ddd', borderRadius: '4px', padding: '8px', maxHeight: '420px', overflowY: 'auto' }}>
                    {loading ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span className="k-icon k-font-icon k-i-loading" />
                            Loading icon list...
                        </div>
                    ) : (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))', gap: '8px' }}>
                            {visibleCodes.map(code => {
                                const entity = `&#${code};`;
                                const isSelected = selectedCode === code;
                                return (
                                    <button
                                        key={code}
                                        type="button"
                                        className="k-button"
                                        onClick={() => setSelectedCode(code)}
                                        title={`Code ${code}`}
                                        style={{
                                            height: '64px',
                                            border: isSelected ? '2px solid #0d6efd' : '1px solid #ccc',
                                            background: isSelected ? '#eef5ff' : '#fff',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '2px',
                                        }}
                                    >
                                        <span
                                            className={isWebComponentsPrivateUseCode(code) ? 'k-icon k-font-icon' : ''}
                                            dangerouslySetInnerHTML={{ __html: entity }}
                                            style={{
                                                fontSize: '18px',
                                                lineHeight: 1,
                                                fontFamily: isWebComponentsPrivateUseCode(code) ? 'WebComponentsIcons' : 'inherit',
                                            }}
                                        />
                                        <span style={{ fontSize: '10px', color: '#555' }}>{code}</span>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
            <DialogActionsBar>
                <button
                    className="k-button k-button-solid-base"
                    onClick={() => setSelectedCode(null)}
                    disabled={loading}
                >
                    Clear Icon
                </button>
                <button className="k-button k-button-solid-primary" onClick={handleApply} disabled={loading}>
                    Use Selected Icon
                </button>
                <button className="k-button k-button-solid-base" onClick={onClose}>
                    Cancel
                </button>
            </DialogActionsBar>
        </Dialog>
    );
};

export default IconPickerDialog;
